import { navigate } from "gatsby";
import React, {
	type PropsWithChildren,
	useContext,
	useEffect,
	useState,
} from "react";
import Modal from "react-modal";
import { Button } from "../../../components/common";
import { useMyAccountUK } from "../../../context";
import { formatDateTime } from "../../../utils/dates";
import { isSpecificSite } from "../../../utils/isSpecificSite";
import SSRSuspense from "../../SSRSuspense/SSRSuspense";
import * as styles from "./FormBuilder.module.scss";

type NoSubmitReason = "offline" | "wait";

// This is a custom hook that allows you to get access to the form builder context.
// It is used to set whether the form has been submitted.
// This is used to determine whether the form has been submitted when the form is being validated.

export interface FormBuilderContextState {
	setHasSubmitted: (hasSubmitted: boolean) => void;
	hasSubmitted: boolean;
	formBlockedReason?: {
		message: string;
		reason: NoSubmitReason;
	};
	previousUrl?: string | null;
	isLastStep: boolean;
}
const FormBuilderContext = React.createContext<FormBuilderContextState>(
	{} as FormBuilderContextState,
);

interface FormBuilderContextProviderProps extends PropsWithChildren {
	isLastStep: boolean;
	previousUrl: string | null;
}

export const FormBuilderContextProvider: React.FC<
	FormBuilderContextProviderProps
> = ({ children, isLastStep, previousUrl }) => {
	isSpecificSite("uk", "FormBuilderContextProvider");
	const {
		profile: { userId, adoptionSubmissions },
		canSubmitAdoptionApplication,
	} = useMyAccountUK();
	const [hasSubmitted, setHasSubmitted] = useState(false);
	const [formBlockedReason, setFormBlockedReason] = useState<
		| {
				message: string;
				reason: NoSubmitReason;
		  }
		| undefined
	>(undefined);
	useEffect(() => {
		if (userId === "offline1") {
			setFormBlockedReason({
				message:
					"<p>You must be logged in to submit an adoption application.</p>",
				reason: "offline",
			});
		} else if (!canSubmitAdoptionApplication) {
			// The submission date strings
			const submissions = adoptionSubmissions || [];
			// Get the most recent submission
			const mostRecentSubmission = submissions.sort((a, b) => b - a)[0];
			const mostRecentSubmissionDate = new Date(mostRecentSubmission);
			const nextSubmissionDate = mostRecentSubmissionDate;

			// The next submission date is the most recent submission date plus 3 months
			nextSubmissionDate.setMonth(mostRecentSubmissionDate.getMonth() + 3);
			setFormBlockedReason({
				message: `<p>You can only register to adopt once every three months.</p><p>Please wait until ${formatDateTime(
					nextSubmissionDate,
				)}</p>`,
				reason: "wait",
			});
		}
	}, [userId, canSubmitAdoptionApplication, adoptionSubmissions]);

	useEffect(() => {
		if (formBlockedReason) {
			document.getElementsByTagName("body")[0].style.overflow = "hidden";
		} else {
			document.getElementsByTagName("body")[0].style.overflow = "scroll";
		}
		return () => {
			document.getElementsByTagName("body")[0].style.overflow = "scroll";
		};
	}, [formBlockedReason]);

	const customStyles = {
		content: {
			padding: "50px 30px",
			top: "50%",
			left: "50%",
			right: "auto",
			bottom: "auto",
			marginRight: "-50%",
			transform: "translate(-50%, -50%)",
			border: "0",
			borderRadius: "0",
			boxShadow: "0px 4px 34px 0px rgba(0, 0, 0, 0.25)",
			maxHeight: "80vh",
			maxWidth: "75vw",
		},
		overlay: {
			zIndex: "998",
		},
	};
	const onNonSubmit = () => {
		if (formBlockedReason?.reason === "offline") {
			navigate("/api/my-account/");
		} else if (formBlockedReason?.reason === "wait") {
			navigate("/rehoming");
		}
	};

	const getMessage = () => {
		if (formBlockedReason?.reason === "offline") {
			return "Go to login";
		}
		if (formBlockedReason?.reason === "wait") {
			return "Return to Dogs Trust Rehoming";
		}
	};
	return (
		<FormBuilderContext.Provider
			value={{
				setHasSubmitted,
				formBlockedReason,
				hasSubmitted,
				isLastStep,
				previousUrl,
			}}
		>
			{children}
			<SSRSuspense>
				<Modal
					isOpen={!!formBlockedReason}
					style={customStyles}
					preventScroll={true}
					shouldCloseOnEsc={true}
					shouldCloseOnOverlayClick={true}
					shouldFocusAfterRender={true}
					shouldReturnFocusAfterClose={true}
					ariaHideApp={false}
				>
					<div className={styles.submissionblockedwarningmodal}>
						<h3 className={styles.modaltitle}>Form submission not available</h3>
						<div
							// biome-ignore lint/security/noDangerouslySetInnerHtml: It's only going to be the content above
							dangerouslySetInnerHTML={{
								__html: formBlockedReason?.message || "",
							}}
						/>
						<div className={styles.submissionblockedwarningmodalcta}>
							<Button variant="primary" onClick={onNonSubmit}>
								{getMessage()}
							</Button>
						</div>
					</div>
				</Modal>
			</SSRSuspense>
		</FormBuilderContext.Provider>
	);
};

export const useFormBuilderContext = () => useContext(FormBuilderContext);
